/* @flow */
import * as React from "react";
import Model from "nexia_mobile/models/base_model";
import Dispatch from "nexia_mobile/flux_like/dispatch";
import type { PromiseWithAbort } from "nexia_mobile/PromiseWithAbort";
import type { FetchOptions } from "nexia_mobile/framework/resource";

type FetchManagerChildren = ({
  // Whether a spinner should be shown right now. It's up to the children to use
  // this to draw a spinner.
  spinnerShown: boolean,

  // What message to show with the spinner. Only really makes sense if the
  // spinner is shown.
  spinnerMessage: ?string,

  // Whether an indeterminate progress indicator bar should be shown right now.
  // It's up to the children to use this to draw one.
  progressShown: boolean,

  // Call to show or hide the spinner. Won't do anything if the component has
  // been unmounted. Pass a string into showSpinner to set the spinner message.
  // (It just updates spinnerShown.)
  showSpinner: (?string) => void,
  hideSpinner: () => void,

  // Call to show or hide the progress indicator. Won't do anything if the
  // component has been unmounted. (It just updates progressShown.)
  showProgress: () => void,
  hideProgress: () => void,

  // Wrapper around showSpinner and hideSpinner that makes it easier to perform
  // the most common thing you use them for: call showSpinner, then start an
  // operation that uses a Promise, and call hideSpinner when that operation
  // completes or fails. Pass in a function that returns the Promise you want to
  // show a spinner around.
  runPromiseWithSpinner: (() => Promise<any>) => void,

  // Wrapper around showProgress and hideProgress that makes it easier to
  // perform the most common thing you use them for: call showProgress, then
  // start an operation that uses a Promise, and call hideProgress when that
  // operation completes or fails. Pass in a function that returns the Promise
  // you want to show the progress indicator around.
  runPromiseWithProgress: (() => Promise<any>) => void,

  // Call these to fetch the details, model and details for the
  // provided device/location/automation. These resolve immediately if the
  // component is not mounted, and if a fetch is already in progress, it will
  // return the in-progress Promise instead of fetching again.
  fetchDetails: () => Promise<void>,
  fetchModelAndMaybeDetails: () => Promise<void>,

  // Whether the FetchManager is currently fetching details, model and maybe
  // details, you don't need to defensively check these keys before
  // you call fetchDetails or fetchModelAndMaybeDetails; the
  // FetchManager will ensure that only one request goes out at once. These are
  // only provided in case you need to use the state about a particular request
  // in your render.
  isFetchingDetails: boolean,
  isFetchingModelAndMaybeDetails: boolean,
}) => React.Element<any>;

type Props = {
  model: Model,
  children: FetchManagerChildren,
};

type State = {
  spinnerShown: boolean,
  spinnerMessage: ?string,
  progressShown: boolean,
  isFetchingDetails: boolean,
  isFetchingModelAndMaybeDetails: boolean,
};

export default class FetchManager extends React.Component<Props, State> {
  state = {
    spinnerShown: false,
    spinnerMessage: null,
    progressShown: false,
    isFetchingDetails: false,
    isFetchingModelAndMaybeDetails: false,
  };

  componentDidMount() {
    const { model } = this.props;

    this._isMounted = true;

    if (typeof model.hasDetailsLink === "function" && model.hasDetailsLink()) {
      this._runPromiseWithProgress(this._fetchDetails);
      // HACK: Don't poll the location while showing this device, because
      // polling the location would get the details-less attributes for this
      // device, and setting those onto this model would override the attributes
      // we got from the details fetch (because the server provides null values
      // for those attributes when we aren't fetching details). So the UI would
      // switch back and forth between having details and not having it, which
      // looks broken.
      this._disablePolling();

      // That said, we still want to update this device's status every 20
      // seconds, so fetch this device (and its details) when the location poll
      // timer hits.
      Dispatch.on(
        "LOCATION_POLL_TIMER",
        this._fetchModelAndMaybeDetailsIfMounted,
        this
      );
    }
  }

  componentWillUnmount() {
    this._isMounted = false;

    Object.values(this._runningPromises).forEach((promiseWithAbort) => {
      if (
        promiseWithAbort != null &&
        typeof promiseWithAbort === "object" &&
        typeof promiseWithAbort.abort === "function"
      ) {
        promiseWithAbort.abort();
      }
    });

    if (this._pollingDisabledStack !== 0) {
      Dispatch("ENABLE_LOCATION_POLLING");
    }

    Dispatch.off(null, null, this);
  }

  _isMounted: boolean;
  _pollingDisabledStack = 0;
  _runningPromises: { [key: string]: PromiseWithAbort<*> | null };
  _runningPromises = {};

  _ifMounted<MountedReturnType, UnmountedReturnType>(
    mountedCallback: (...Array<any>) => MountedReturnType,
    unmountedCallback: ?(...Array<any>) => UnmountedReturnType
  ): (...Array<any>) => MountedReturnType | UnmountedReturnType {
    const callIfMounted = (...args: Array<any>) => {
      if (this._isMounted) {
        return mountedCallback(...args);
      } else if (unmountedCallback != null) {
        return unmountedCallback(...args);
      } else {
        return undefined;
      }
    };
    return callIfMounted;
  }

  _disablePolling = () => {
    if (this._pollingDisabledStack === 0) {
      Dispatch("DISABLE_LOCATION_POLLING");
    }
    this._pollingDisabledStack++;
  };

  _enablePolling = this._ifMounted(() => {
    this._pollingDisabledStack--;
    if (this._pollingDisabledStack <= 0) {
      this._pollingDisabledStack = 0;
      Dispatch("ENABLE_LOCATION_POLLING");
    }
  });

  _setStateIfMounted = this._ifMounted(this.setState.bind(this));

  _showSpinner = this._ifMounted((spinnerMessage) => {
    this.setState({ spinnerShown: true, spinnerMessage });
  });

  _hideSpinner = this._ifMounted(() => {
    this.setState({ spinnerShown: false, spinnerMessage: null });
  });

  _runPromiseWithSpinner = (callback: () => Promise<*>, message: ?string) => {
    this._showSpinner(message);
    callback().then(this._hideSpinner, this._hideSpinner);
  };

  _showProgress = this._ifMounted(() => {
    this.setState({ progressShown: true });
  });

  _hideProgress = this._ifMounted(() => {
    this.setState({ progressShown: false });
  });

  _runPromiseWithProgress = (callback: () => Promise<any>) => {
    this._showProgress();
    callback().then(this._hideProgress, this._hideProgress);
  };

  _runPromiseWithAbortAndSaveToPropertyWithStateKey(
    callback: () => PromiseWithAbort<any>,
    propertyName: string,
    stateKey: string
  ): Promise<void> {
    if (this._runningPromises[propertyName] != null) {
      return this._runningPromises[propertyName];
    }

    const promiseWithAbort = callback();
    this._runningPromises[propertyName] = promiseWithAbort;
    this._setStateIfMounted({ [stateKey]: true });
    const removeReference = () => {
      this._runningPromises[propertyName] = null;
      this._setStateIfMounted({ [stateKey]: false });
    };
    return promiseWithAbort.then(removeReference).catch(removeReference);

    // Note: we don't setState on abort because we only abort in willUnmount,
    // and we don't want to schedule an unnecessary state update for a
    // component that is being unmounted.
  }

  _fetchModelAndMaybeDetails = (
    fetchOptions: FetchOptions = {}
  ): Promise<void> => {
    this._disablePolling();
    return this._runPromiseWithAbortAndSaveToPropertyWithStateKey(
      () => {
        const model = this.props.model;
        return model.fetchWithDetails(fetchOptions).then(() => {
          model.populatePendingRequestsRecursively();
        });
      },
      "_modelAndDetailsPromise",
      "isFetchingModelAndMaybeDetails"
    )
      .then(this._enablePolling)
      .catch((reason) => {
        this._enablePolling();
        throw reason;
      });
  };

  _fetchModelAndMaybeDetailsIfMounted = this._ifMounted(
    this._fetchModelAndMaybeDetails,
    (): Promise<void> => Promise.resolve()
  );

  _fetchDetails = (fetchOptions: FetchOptions = {}): Promise<void> => {
    this._disablePolling();
    return this._runPromiseWithAbortAndSaveToPropertyWithStateKey(
      () => {
        return this.props.model.fetchDetails(fetchOptions);
      },
      "_detailsPromise",
      "isFetchingDetails"
    )
      .then(this._enablePolling)
      .catch((reason) => {
        this._enablePolling();
        throw reason;
      });
  };

  _fetchDetailsIfMounted = this._ifMounted(
    this._fetchDetails,
    (): Promise<void> => Promise.resolve()
  );

  render() {
    return this.props.children({
      ...this.state,
      showSpinner: this._showSpinner,
      hideSpinner: this._hideSpinner,
      showProgress: this._showProgress,
      hideProgress: this._hideProgress,
      runPromiseWithSpinner: this._runPromiseWithSpinner,
      runPromiseWithProgress: this._runPromiseWithProgress,
      fetchDetails: this._fetchDetailsIfMounted,
      fetchModelAndMaybeDetails: this._fetchModelAndMaybeDetailsIfMounted,
    });
  }
}



// WEBPACK FOOTER //
// ./src/components/Details/FetchManager.jsx